'use client';
import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { twJoin, twMerge } from 'tailwind-merge';
// HOOKS
import { useScroll } from '@/context/scrollContext';

import { Button, Heading, Svg, Text } from '@/components';
import { HeartOutlineIcon } from '@/icons';
import { useCart } from '@/context/cartContext';
import { useToggle } from '@/hooks';
import { IMAGE_URI } from '@/config/endpoints';
import {
  checkItemInWishlist,
  getBasicCartRequest,
  getKeyValue,
  getUrlParamsStr,
  getWeightWithUnit,
  googleDataLayerEventPush,
  pushGA4Event,
} from '@/utils/helper';
import { OUT_OF_STOCK_LABEL, PROUDCT_ATTRIBUTE_SETS_ENUMS } from '@/constants';
import { useWishlist } from '@/context/wishlistContext';
import { ToggleCountField } from '../Inputs';
import { usePathname } from 'next/navigation';
import { searchParams } from '@/utils/helper';
import { logger, storage } from '@/config';
import SelectOptionProductCardWizzy from '@/features/ProductDetails/components/selectOptionProductCardWizzy';
import {
  ENTITY_TYPE_ENUMS,
  PRODUCT_TYPE_ENUMS,
} from '@/features/ProductDetails/constants/ProductType';
import { toast } from 'react-toastify';

import { useCleverTap } from '@/context/clevertapContext';
import {
  buildAddedToCartEventData,
  getProductCategory,
  getEffectivePrice,
  getProductImageUrl,
} from '@/utils/clevertapEvents';
import httpFetch from '@/config/httpFetch';
import { useHeaderContext } from '@/context/headerContext';
import useMakeYourGiftStore from '@/store/make-your-gift-store';

interface PropTypes {
  item: any;
  inShop?: boolean;
  toggleCartSidebar?: (value: boolean) => void;
  classNames?: { root?: string; textContainer?: string; button?: string };
  plpCard?: boolean;
  eventMetadata?: {
    sectionName?: string;
    rank?: number;
  };
  dataLayerSource?:
    | 'ARGOID_CART_RECOMMENDATIONS'
    | 'ARGOID_PLP'
    | 'ARGOID_SEARCH_DIRECT'
    | 'ARGOID_SEARCH_AUTOCOMPLETE'
    | 'ARGOID_HOME_TRENDING'
    | 'ARGOID_PDP_SIMILAR_PRODUCTS'
    | 'ARGOID_PDP_COMPLEMENTING_PRODUCTS';
  searchResult?: 'DIRECT' | 'AUTO_COMPLETE';
  index?: number;
  wizzyEventData?: {
    name?: string;
    wizzyCampaignId?: string | null;
    wizzySearchResponseId?: string | null;
    wizzySource?:
      | 'AUTOCOMPLETE_RESULTS'
      | 'AUTOCOMPLETE_DEFAULT'
      | 'SEARCH_RESULTS'
      | 'NO_RESULTS_FOUND'
      | 'FEATURED_VIEW';
  };
  favourite?: boolean;
  pageIdx?: number;
  priority?: boolean;
  version?: number;
  isBuildYourBoxPage?: boolean;
}

interface ButtonPropTypes {
  label: 'Add to Cart' | 'Gift Now' | 'Select';
  disabled?: boolean;
}

const ProductCardWizzy: FC<PropTypes> = ({
  item,
  inShop,
  toggleCartSidebar,
  classNames,
  plpCard,
  eventMetadata,
  dataLayerSource,
  searchResult,
  wizzyEventData,
  index,
  favourite = false,
  priority,
  version = 1,
  isBuildYourBoxPage = false,
}) => {
  const pathname = usePathname();
  const isBuildBox = isBuildYourBoxPage;
  const { totalItemsSelected, selectedBox } = useMakeYourGiftStore();
  const maxItemsSelectedForBox =
    isBuildYourBoxPage && totalItemsSelected >= (selectedBox?.boxCapacity ?? 0);

  const { toggleIsSearchDrawerOpen } = useHeaderContext();
  const { saveScrollPositionImmediate } = useScroll();
  const [selectedVariant, setSelectedVariant] = useState<any>(item);
  const customAttributes = useMemo(
    () => getKeyValue(selectedVariant?.custom_attributes, 'attribute_code'),
    [selectedVariant]
  );
  const stockQty = customAttributes?.wz_stock_qty;
  const tag_one = item?.tag_one || customAttributes?.tag_one;
  const tag_two = item?.tag_two || customAttributes?.tag_two;
  const url_key = item?.url_key ?? customAttributes?.url_key;
  const entityType = customAttributes?.wz_attribute_set_id?.toLowerCase();
  const { cartItems, cartItemsMap, cartValue, removeFromCart, addToCart } =
    useCart();
  const { productsMap, addProduct, updateProduct, removeProduct } =
    useMakeYourGiftStore();
  const { pushEvent } = useCleverTap();
  const query = searchParams.get('query');
  const { addToWishlist, removeFromWishlist, wishlistItems } = useWishlist();
  const [itemId, setItemId] = useState<number>();
  const [variantsOptions, setVariantsOptions] = useState<any[]>([]);
  // Check if the product is already inside a hamper (as a gift box item)
  // If so, we treat it as a separate product and allow adding to cart independently
  const isInsideHamper = cartItems?.some(
    (item: any) =>
      selectedVariant?.sku === item?.sku &&
      item.extension_attributes?.is_gift_hamper === '0' &&
      item.extension_attributes?.gift_box_item_id
  );

  const isLockedByGiftBox =
    !isBuildBox &&
    cartItems?.some(
      (item: any) =>
        selectedVariant?.sku === item?.sku &&
        item.extension_attributes?.is_gift_hamper === '0' &&
        item.extension_attributes?.gift_box_item_id &&
        item?.qty === item.extension_attributes?.available_qty
    );

  // Get the separate cart item (not part of hamper) for quantity updates
  const separateCartItem = cartItems?.find(
    (item: any) =>
      item.sku === selectedVariant?.sku &&
      (!item.extension_attributes?.gift_box_item_id ||
        item.extension_attributes?.is_gift_hamper === '1')
  );

  const itemExistsInCart: any = useMemo(() => {
    if (isBuildBox) {
      return productsMap[selectedVariant?.sku];
    }

    const cartItem = cartItemsMap.get(selectedVariant?.sku);

    // If item exists in cart but is inside a hamper, treat it as not in cart
    // unless there's also a separate cart item
    const shouldTreatAsNotInCart =
      cartItem && isInsideHamper && !separateCartItem;

    return shouldTreatAsNotInCart
      ? false
      : separateCartItem || cartItem || false;
  }, [
    isBuildBox,
    productsMap,
    cartItemsMap,
    selectedVariant?.sku,
    isInsideHamper,
    separateCartItem,
  ]);
  const [disableAtc, setDisableAtc] = useState<boolean>(false);

  const [buttonProps, setButtonProps] = useState<ButtonPropTypes>({
    label: 'Add to Cart',
    disabled: false,
  });

  const quote_id = storage.get('quote_id');

  const isWishlisted = checkItemInWishlist(wishlistItems, selectedVariant?.sku);

  const { value: isInCart } = useToggle(false);

  const itemInCart = () => {
    if (itemExistsInCart && !isBuildBox) {
      setItemId(itemExistsInCart?.item_id);
    } else {
      setItemId(undefined);
      const label =
        entityType === PROUDCT_ATTRIBUTE_SETS_ENUMS.giftHampers
          ? 'Gift Now'
          : !isBuildBox
            ? 'Add to Cart'
            : 'Select';
      setButtonProps({
        label,
      });
    }
  };

  const image = customAttributes?.thumbnail;
  const imageUrl = !image
    ? ''
    : `${process.env.NEXT_PUBLIC_CDN_LINK}${IMAGE_URI}${image}`;

  const request = useMemo(() => {
    const req = getBasicCartRequest(selectedVariant);
    if (quote_id) {
      req.quote_id = quote_id;
    }
    if (
      entityType === PROUDCT_ATTRIBUTE_SETS_ENUMS.giftHampers &&
      item?.options
    ) {
      const attributes =
        typeof item?.options === 'string'
          ? JSON.parse(item?.options)?.[0]
          : item?.options?.[0];
      req.gift.option_id = attributes?.option_id;
      req.gift.option_value = '1';
    }
    if (item.type_id === PRODUCT_TYPE_ENUMS.configurable) {
      req.sku = selectedVariant?.sku;
    }
    itemInCart();
    return req;
  }, [selectedVariant, quote_id]);

  const hasMaxLimit = customAttributes?.max_allowed_qty;
  const reachedMaxLimit =
    hasMaxLimit &&
    itemExistsInCart &&
    itemExistsInCart?.qty >= Number(hasMaxLimit);

  const UserActions = () => {
    return (
      <>
        {!itemExistsInCart || itemExistsInCart?.qty === 0 ? (
          <div
            className={twMerge(
              'relative w-full transition duration-200 ease-in-out'
            )}>
            <Button
              id='add-to-cart-button'
              style={{ backgroundColor: '#A0383F' }}
              className={twMerge(
                'text-sm font-bold text-white disabled:opacity-70 max-md:py-[0.72rem] min-h-12 hover:opacity-90',
                plpCard ? 'max-md:py-[0.54rem]' : '',
                version === 2 ? 'font-semibold text-base h-auto' : '',
                classNames?.button
              )}
              label={buttonProps.label}
              onClick={onCart}
              isLoading={itemExistsInCart && itemExistsInCart?.isLoading}
              disabled={
                (itemExistsInCart && itemExistsInCart?.isLoading) ||
                selectedVariant?.stock_status === 'OUT_OF_STOCK' ||
                maxItemsSelectedForBox ||
                (isBuildBox && stockQty && stockQty <= 0) ||
                isLockedByGiftBox
              }
            />
          </div>
        ) : (
          <div className='h-full w-full rounded-full bg-pale-rose'>
            <ToggleCountField
              disabled={
                itemExistsInCart?.isLoading ||
                selectedVariant?.stock_status === 'OUT_OF_STOCK' ||
                (isBuildBox && stockQty && stockQty <= 0) ||
                isLockedByGiftBox
              }
              className='p-2'
              OutofStock={selectedVariant?.stock_status === 'OUT_OF_STOCK'}
              disableAdd={
                reachedMaxLimit ||
                maxItemsSelectedForBox ||
                (isBuildBox && stockQty && itemExistsInCart?.qty >= stockQty)
              }
              getCount={async (newValue: number) => {
                try {
                  // Check stock quantity before updating (only for build box)
                  if (isBuildBox && stockQty && newValue > stockQty) {
                    toast.error(
                      `Cannot add more than ${stockQty} items. Only ${stockQty} items available in stock.`
                    );
                    return;
                  }
                  request.qty = newValue;
                  request.item_id = itemId;
                  if (isBuildBox) {
                    if (newValue === 0) {
                      removeProduct(selectedVariant.sku);
                    } else {
                      updateProduct(selectedVariant.sku, newValue);
                    }
                    return;
                  }

                  // Use the separate cart item for updates, not the hamper item
                  const cartItemToUpdate = separateCartItem || itemExistsInCart;

                  if (newValue === 0) {
                    await removeFromCart(cartItemToUpdate);
                    return;
                  }

                  const req = await addToCart(
                    { ...request, item_id: cartItemToUpdate?.item_id },
                    'put'
                  );
                  if (!req.success) {
                    throw new Error('Failed to update cart');
                  }

                  // Only update analytics if the request was successful
                  pushEvent({
                    event:
                      itemExistsInCart && !isInsideHamper
                        ? 'Updated Product In Cart'
                        : 'Added Product To Cart',
                    data:
                      itemExistsInCart && !isInsideHamper
                        ? {
                            ['Timestamp']: new Date(),
                            ['Product Name']: item?.name,
                            ['Product ID']: request?.sku,
                            ['Product Price']: getEffectivePrice(
                              customAttributes,
                              selectedVariant?.price
                            ),
                            ['Updated Quantity']: newValue,
                            ['Cart Amount']:
                              itemExistsInCart?.qty &&
                              newValue > itemExistsInCart?.qty
                                ? cartValue +
                                  Number(
                                    getEffectivePrice(
                                      customAttributes,
                                      item?.price
                                    )
                                  )
                                : cartValue -
                                  Number(
                                    getEffectivePrice(
                                      customAttributes,
                                      item?.price
                                    )
                                  ),
                          }
                        : buildAddedToCartEventData({
                            productName: item?.name,
                            productId: request?.sku,
                            productPrice: getEffectivePrice(
                              customAttributes,
                              selectedVariant?.price
                            ),
                            quantity: '1',
                            cartAmount:
                              cartValue +
                              Number(
                                getEffectivePrice(customAttributes, item?.price)
                              ),
                            source: 'PLP',
                            category: getProductCategory(
                              item,
                              customAttributes
                            ),
                            productImageUrl:
                              getProductImageUrl(customAttributes),
                            pathname,
                          }),
                  });
                  wizzyConvertedEvent('Product Added to Cart', newValue);
                } catch (error) {
                  logger.error(error);
                  throw error; // Re-throw to let ToggleCountField handle the error
                }
              }}
              defaultValue={itemExistsInCart?.qty || 1}
              variant='PLP'
            />
          </div>
        )}
      </>
    );
  };

  const pageParams: any = {};
  if (item?.type_id === PRODUCT_TYPE_ENUMS.configurable) {
    pageParams.selected = selectedVariant?.sku;
  }
  if (inShop) {
    pageParams.breadcrumb = `${pathname}/`;
  }
  if (query) {
    pageParams.query = query;
  }

  function handleVariantChange(option: any) {
    setSelectedVariant(option);
  }

  const getRedirectUrl = useCallback(() => {
    const basePath =
      entityType === ENTITY_TYPE_ENUMS.GIFT_HAMPERS ? '/gifting' : '/shop';

    // Prepare search params
    const searchParams = { ...pageParams };

    // Add wizzyEventData to searchParams if available
    if (wizzyEventData) {
      const { wizzyCampaignId, wizzySearchResponseId, wizzySource } =
        wizzyEventData;
      if (wizzyCampaignId) searchParams.wizzyCampaignId = wizzyCampaignId;
      if (wizzySearchResponseId)
        searchParams.wizzySearchResponseId = wizzySearchResponseId;
      if (wizzySource) searchParams.wizzySource = wizzySource;
    }

    // Convert searchParams to query string
    const queryString = Object.keys(searchParams).length
      ? `?${getUrlParamsStr(searchParams)}`
      : '';

    const url = url_key?.startsWith('/')
      ? `${basePath}/product${url_key}/${queryString}`
      : `${basePath}/product/${url_key}/${queryString}`;
    return url;
  }, [entityType, pageParams, url_key, wizzyEventData]);

  const wizzyClickEvent = useCallback(async () => {
    const payload = {
      name: wizzyEventData?.name || 'Product Viewed',
      items: [
        {
          itemId: String(selectedVariant?.id),
          position: eventMetadata?.rank || index || '1',
        },
      ],
      triggeredOn: new Date().toISOString().slice(0, 19).replace('T', ' '),
      searchResponseId:
        wizzyEventData?.wizzySearchResponseId ||
        sessionStorage.getItem('wizzyResponseId') ||
        undefined,
      campaignId: wizzyEventData?.wizzyCampaignId,
      source: wizzyEventData?.wizzySource,
    };
    await httpFetch.post('/api/wizzy/event/click', payload);
  }, [selectedVariant, wizzyEventData]);

  const wizzyConvertedEvent = useCallback(
    async (convertedEventName: string, newQty: number = 1) => {
      const payload = {
        name: convertedEventName,
        items: [
          {
            itemId: String(selectedVariant?.id),
            qty: newQty,
          },
        ],
        triggeredOn: new Date().toISOString().slice(0, 19).replace('T', ' '),
        searchResponseId:
          wizzyEventData?.wizzySearchResponseId ||
          sessionStorage.getItem('wizzyResponseId') ||
          undefined,
        campaignId: wizzyEventData?.wizzyCampaignId,
        source: wizzyEventData?.wizzySource,
        value: customAttributes?.special_price
          ? Number(customAttributes?.special_price)?.toFixed(0)
          : Number(selectedVariant?.price)?.toFixed(0),
        qty: newQty,
        id: String(selectedVariant?.id),
      };
      await httpFetch.post('/api/wizzy/event/converted/', payload);
    },
    [selectedVariant, wizzyEventData, customAttributes, itemExistsInCart]
  );

  const fireCrossSellGAEvents = (
    source: PropTypes['dataLayerSource'],
    sectionName: string | undefined,
    sku: string | undefined
  ) => {
    if (!sku) return;

    if (
      source === 'ARGOID_PDP_SIMILAR_PRODUCTS' ||
      source === 'ARGOID_PDP_COMPLEMENTING_PRODUCTS'
    ) {
      pushGA4Event('cross_sell_add_to_cart_pdp', {
        category: 'Cross Sell',
        item_id: sku,
      });
      pushGA4Event('cross_sell_add_to_cart_all', {
        category: 'Cross Sell',
        item_id: sku,
      });
    } else if (sectionName === 'Checkout Recommendations') {
      pushGA4Event('cross_sell_add_to_cart_checkout', {
        category: 'Cross Sell',
        item_id: sku,
      });
      pushGA4Event('cross_sell_add_to_cart_all', {
        category: 'Cross Sell',
        item_id: sku,
      });
    } else if (
      source === 'ARGOID_CART_RECOMMENDATIONS' ||
      sectionName === 'Cart Recommendations'
    ) {
      pushGA4Event('cross_sell_add_to_cart_cart', {
        category: 'Cross Sell',
        item_id: sku,
      });
      pushGA4Event('cross_sell_add_to_cart_all', {
        category: 'Cross Sell',
        item_id: sku,
      });
    } else if (source) {
      pushGA4Event('cross_sell_add_to_cart_widget', {
        category: 'Cross Sell',
        item_id: sku,
        widget: source,
      });
      pushGA4Event('cross_sell_add_to_cart_all', {
        category: 'Cross Sell',
        item_id: sku,
      });
    }
  };

  const onCart = useCallback(
    async (
      e:
        | React.MouseEvent<HTMLButtonElement>
        | React.TouchEvent<HTMLButtonElement>
    ) => {
      const searchQuery = (
        document.getElementById('searchBar') as HTMLInputElement
      )?.value;
      e?.preventDefault();
      e?.stopPropagation();
      setDisableAtc(true);

      if (isBuildBox) {
        // For build box, check stock quantity before adding to cart
        if (stockQty && stockQty <= 0) {
          setDisableAtc(false);
          return;
        }

        // For build box, check if adding 1 more item would exceed stock
        const currentQty = productsMap[selectedVariant?.sku]?.qty || 0;
        if (stockQty && currentQty + 1 > stockQty) {
          setDisableAtc(false);
          return;
        }

        addProduct({
          sku: selectedVariant.sku,
          qty: 1,
          stockQty: stockQty,
          name: selectedVariant.name,
          image: imageUrl,
          price: Number(selectedVariant?.price),
          specialPrice: customAttributes?.special_price
            ? Number(customAttributes?.special_price)
            : undefined,
          isGiftHamper: entityType === PROUDCT_ATTRIBUTE_SETS_ENUMS.giftHampers,
        });
        setDisableAtc(false);
        return;
      }
      const atcEventData: any = {
        ['Timestamp']: new Date(),
        ['Product Name']: item?.name,
        ['Product ID']: request?.sku,
        ['Product Price']: customAttributes?.special_price
          ? customAttributes?.special_price
          : selectedVariant?.price,
        ['Updated Quantity']: '1',
        ['Cart Amount']:
          cartValue +
          Number(
            customAttributes?.special_price
              ? customAttributes?.special_price
              : selectedVariant?.price
          ),
        Pathname: pathname,
        ['Store Unique SKU']: `${request?.sku}${storage.get('store_code')}`,
      };
      pushEvent({
        event: 'Add To Cart Clicked',
        data: atcEventData,
      });
      const req = await addToCart({ ...request, qty: 1 }, 'post', false);
      setDisableAtc(false);
      if (req.success) {
        if (dataLayerSource) {
          googleDataLayerEventPush({
            eventName: 'add_to_cart',
            eventParameters: {
              currency: 'INR',
              eventSource: dataLayerSource,
              value: customAttributes?.special_price
                ? customAttributes?.special_price
                : selectedVariant?.price,
              items: [selectedVariant],
            },
          });
        }
        const atcEventData = buildAddedToCartEventData({
          productName: item?.name,
          productId: request?.sku,
          productPrice: getEffectivePrice(
            customAttributes,
            selectedVariant?.price
          ),
          quantity: '1',
          cartAmount:
            cartValue +
            Number(getEffectivePrice(customAttributes, selectedVariant?.price)),
          source: 'PLP',
          category: getProductCategory(item, customAttributes),
          productImageUrl: getProductImageUrl(customAttributes),
          pathname,
          section: eventMetadata?.sectionName,
        });
        pushEvent({
          event: 'Added Product To Cart',
          data: atcEventData,
        });
        if (eventMetadata?.sectionName) {
          const dataToPush: any = {
            'Section Name': eventMetadata?.sectionName,
            Type: 'Add to Cart',
            ['Product Name']: item?.name,
            ['Product ID']: request?.sku,
            ['Product Price']: customAttributes?.special_price
              ? customAttributes?.special_price
              : selectedVariant?.price,
            Pathname: pathname,
          };
          if (eventMetadata?.rank) {
            dataToPush['Rank'] = eventMetadata?.rank;
          }
          pushEvent({
            event: 'Product Carousel Clicked',
            data: dataToPush,
          });
        }
        if (searchResult) {
          pushEvent({
            event: 'Search Result Add To Cart',
            data: {
              'Search Keyword': searchQuery,
              'Search Type': searchResult,
              'Product SKU': item?.sku,
              'Product Name': item?.name,
              'Product Type': 'Product',
              'Product ID': item?.id,
              Position: index !== undefined ? index + 1 : undefined,
            },
          });
        }
        wizzyConvertedEvent('Product Added to Cart');
        fireCrossSellGAEvents(
          dataLayerSource,
          eventMetadata?.sectionName,
          selectedVariant?.sku || item?.sku
        );
      }
    },
    [
      addToCart,
      cartValue,
      customAttributes?.special_price,
      dataLayerSource,
      eventMetadata,
      item,
      pathname,
      pushEvent,
      request,
      searchResult,
      selectedVariant,
      wizzyConvertedEvent,
      isBuildBox,
    ]
  );

  const toggleWishlist = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      const searchQuery = (
        document.getElementById('searchBar') as HTMLInputElement
      )?.value;
      e.preventDefault();
      e.stopPropagation();
      if (!isWishlisted) {
        addToWishlist(selectedVariant);
        if (eventMetadata?.sectionName) {
          const dataToPush: any = {
            'Section Name': eventMetadata?.sectionName,
            Type: 'Wishlist',
            ['Product Name']: item?.name,
            ['Product ID']: request?.sku,
            ['Product Price']: customAttributes?.special_price
              ? customAttributes?.special_price
              : selectedVariant?.price,
            Pathname: pathname,
          };
          if (eventMetadata?.rank) {
            dataToPush['Rank'] = eventMetadata?.rank;
          }
          pushEvent({
            event: 'Product Carousel Clicked',
            data: dataToPush,
          });
        }
        if (searchResult) {
          pushEvent({
            event: 'Search Result Wishlist',
            data: {
              'Search Keyword': searchQuery,
              'Search Type': searchResult,
              'Product SKU': selectedVariant?.sku,
              'Product Name': selectedVariant?.name,
              'Product Type': 'Product',
            },
          });
        }
        wizzyConvertedEvent('Product Added to Wishlist');
      } else {
        removeFromWishlist(selectedVariant);
      }
    },
    [
      searchResult,
      selectedVariant,
      addToWishlist,
      removeFromWishlist,
      pushEvent,
      wizzyConvertedEvent,
    ]
  );

  useEffect(() => {
    itemInCart();
  }, [cartItems, selectedVariant]);

  useEffect(() => {
    if (item?.type_id === PRODUCT_TYPE_ENUMS.configurable) {
      if (item?.sizes?.length > 1) {
        setVariantsOptions(item?.sizes);
        if (item?.isParentProduct) {
          handleVariantChange(item?.sizes[0]);
        }
      }
    }
    setSelectedVariant(item);
  }, [item]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Save scroll position before navigation
    saveScrollPositionImmediate(window.scrollY);

    // Open in new tab if command (Mac) or control (Windows) is pressed
    if (e.metaKey || e.ctrlKey) {
      const url = getRedirectUrl();
      window.open(url, '_blank');
      return;
    }

    const searchBar = document.getElementById('searchBar') as HTMLInputElement;
    setTimeout(() => searchBar?.blur(), 200);
    const value = searchBar?.value;

    // Close search drawer when clicking on a product
    toggleIsSearchDrawerOpen(false);

    if (dataLayerSource) {
      googleDataLayerEventPush({
        eventName: 'select_item',
        eventParameters: {
          currency: 'INR',
          eventSource: dataLayerSource,
          value: customAttributes?.special_price
            ? customAttributes?.special_price
            : selectedVariant?.price,
          items: [selectedVariant],
        },
      });
    }

    if (searchResult) {
      pushEvent({
        event: 'Search Result Clicked',
        data: {
          'Search Keyword': value,
          'Search Type': searchResult,
          'Product SKU': item?.sku,
          'Product Name': item?.name,
          'Product Type': 'Product',
        },
      });
    }
    wizzyClickEvent();
  };

  const handleLinkClick = () => {
    if (toggleCartSidebar) {
      toggleCartSidebar(false);
    }
    if (eventMetadata?.sectionName) {
      const dataToPush: any = {
        'Section Name': eventMetadata?.sectionName,
        Type: 'Open PDP',
        ['Product Name']: item?.name,
        ['Product ID']: request?.sku,
        ['Product Price']: customAttributes?.special_price
          ? customAttributes?.special_price
          : selectedVariant?.price,
        Pathname: pathname,
      };
      if (eventMetadata?.rank) {
        dataToPush['Rank'] = eventMetadata?.rank;
      }
      pushEvent({
        event: 'Product Carousel Clicked',
        data: dataToPush,
      });
    }
  };

  return (
    <div
      onClick={handleClick}
      key={Number(selectedVariant.id)}
      className={twMerge(
        'group col-span-1 h-full row-span-1 flex w-full max-w-76.5 flex-1 flex-col gap-6 md:gap-9',
        version === 2 && 'bg-white border border-[#393939B]/15 rounded-xl p-3',
        classNames?.root
      )}>
      <Link
        href={isBuildBox ? '' : getRedirectUrl()}
        passHref
        prefetch={false}
        className={twMerge(
          'block space-y-2',
          isBuildBox ? 'cursor-default' : ''
        )}
        onClick={isBuildBox ? e => e.preventDefault() : handleLinkClick}>
        <div
          className={twMerge(
            'relative flex items-center justify-center rounded-lg bg-[#fef9f8] p-0'
          )}>
          {(selectedVariant?.stock_status === 'OUT_OF_STOCK' ||
            isLockedByGiftBox) && (
            <Text className='md:text-md absolute left-3 top-4 z-10 rounded-sm bg-[#38383A] p-3 px-2 py-1 text-xs text-white sm:text-sm'>
              {OUT_OF_STOCK_LABEL}
            </Text>
          )}

          <button
            className={twMerge(
              'visible absolute right-2 top-2 z-10 aspect-square h-8 rounded-full bg-white/60 lg:invisible lg:h-10 lg:group-hover:visible',
              !isInCart ? 'lg:invisible lg:group-hover:visible' : ''
            )}
            id='add-to-wishlist'
            onClick={e => toggleWishlist(e)}>
            <Svg
              viewBox='0 0 16 16'
              className={twJoin(
                `mx-auto aspect-square h-4 cursor-pointer stroke-fig stroke-1 lg:h-5.5`,
                isWishlisted ? 'fill-fig' : 'fill-none'
              )}>
              <HeartOutlineIcon />
            </Svg>
          </button>

          {image && image !== '' ? (
            <div
              className={twMerge(
                'relative aspect-square w-full',
                selectedVariant?.stock_status === 'OUT_OF_STOCK' ||
                  isLockedByGiftBox
                  ? 'opacity-50'
                  : ''
              )}>
              <Image
                alt={selectedVariant.name ?? 'Product'}
                title={selectedVariant.name}
                src={imageUrl || '/placeholder.svg'}
                fill
                sizes='(max-width: 768px) 50vw, 30vw'
                placeholder='empty'
                className='rounded-lg object-cover'
                fetchPriority={priority ? 'high' : 'auto'}
                priority={priority}
                loading={priority ? 'eager' : 'lazy'}
              />
            </div>
          ) : (
            <div className='aspect-square h-full w-full rounded-md bg-[#FEF9F9]' />
          )}
          {item?.stock_status !== 'OUT_OF_STOCK' && (
            <ul
              className={twMerge(
                'absolute left-4 top-4 flex flex-col gap-2 rounded-sm',
                plpCard && 'max-md:left-2 max-md:top-2',
                version === 2 && 'left-2 top-2 gap-1 uppercase'
              )}>
              {tag_one && tag_one !== '' && (
                <li
                  key={'tag_one'}
                  className={twMerge(
                    'w-fit rounded-sm bg-[#38383A] px-2 py-1'
                  )}>
                  <p
                    className={twMerge(
                      'font-gilroy line-clamp-1 text-white',
                      plpCard ? 'max-md:text-xs' : 'text-base',
                      version === 2 ? 'text-[11px] font-semibold' : ''
                    )}>
                    {tag_one}
                  </p>
                </li>
              )}
              {tag_two && tag_two !== '' && (
                <li
                  key={'tag_two'}
                  className={twMerge(
                    'w-fit rounded-sm bg-[#D48A7A] px-2 py-1'
                  )}>
                  <p
                    className={twMerge(
                      'font-gilroy line-clamp-1 text-white',
                      plpCard ? 'max-md:text-xs' : 'text-base',
                      version === 2 ? 'text-[11px] font-semibold' : ''
                    )}>
                    {tag_two}
                  </p>
                </li>
              )}
            </ul>
          )}
        </div>
        <div
          className={twMerge(
            'h-[4.3rem] space-y-2 lg:h-14',
            plpCard ? 'h-[3.7rem] md:h-14' : '',
            classNames?.textContainer
          )}
          aria-label='product-name'>
          <Heading
            className={twMerge(
              'line-clamp-2 px-0 text-left text-lg leading-5.5',
              selectedVariant?.stock_status === 'OUT_OF_STOCK' ||
                isLockedByGiftBox
                ? 'opacity-50'
                : '',
              plpCard ? 'text-sm md:text-lg' : '',
              favourite ? 'text-white' : 'text-black',
              version === 1 ? 'font-semibold' : 'font-medium'
            )}>
            {selectedVariant?.name}
          </Heading>
          <div
            className={twMerge(
              'flex w-full items-center justify-between',
              favourite ? 'text-white' : 'text-black'
            )}>
            <Text
              className={twMerge(
                'px-0 text-left text-lg font-semibold',
                selectedVariant?.stock_status === 'OUT_OF_STOCK'
                  ? 'opacity-50'
                  : '',
                plpCard ? 'text-sm font-bold md:text-lg' : '',
                version === 2 && 'font-semibold text-base'
              )}>
              {customAttributes?.special_price ? (
                <>
                  <span>
                    ₹{Number(customAttributes?.special_price)?.toFixed(0)}
                  </span>
                  {Number(selectedVariant?.price?.toFixed(0)) > 0 && (
                    <span className='ml-3 font-light text-gray-400 line-through'>
                      ₹{Number(selectedVariant?.price)?.toFixed(0)}
                    </span>
                  )}
                </>
              ) : (
                <span>₹{Number(selectedVariant?.price)?.toFixed(0)}</span>
              )}
            </Text>
            {selectedVariant?.type_id === PRODUCT_TYPE_ENUMS.configurable &&
            variantsOptions?.length > 0 ? (
              <div
                className='px-3'
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                }}>
                <SelectOptionProductCardWizzy
                  getSelected={option => handleVariantChange(option)}
                  options={variantsOptions}
                  defaultValue={variantsOptions[0]}
                  item={item}
                  favourite={favourite}
                />
              </div>
            ) : (
              <Text
                className={twMerge(
                  'px-0 text-left text-lg font-semibold',
                  selectedVariant?.stock_status === 'OUT_OF_STOCK'
                    ? 'opacity-50'
                    : '',
                  plpCard ? 'text-sm md:text-lg' : ''
                )}>
                {getWeightWithUnit(
                  selectedVariant?.weight || customAttributes?.weight,
                  selectedVariant?.weight_unit || customAttributes?.weight_unit
                )}
              </Text>
            )}
          </div>
        </div>
      </Link>
      <div
        className={twMerge(
          'visible flex items-center justify-start gap-y-1',
          !disableAtc && (!itemExistsInCart?.qty || itemExistsInCart?.qty === 0)
            ? 'lg:invisible lg:group-hover:visible'
            : disableAtc
              ? 'visible'
              : 'h-full',
          selectedVariant?.stock_status === 'OUT_OF_STOCK' || isLockedByGiftBox
            ? 'opacity-50'
            : '',
          version === 2 && 'lg:visible'
        )}>
        <UserActions />
      </div>
    </div>
  );
};

export default ProductCardWizzy;
